import { AIRTABLE_API_KEY, AIRTABLE_BASE_ID, AIRTABLE_ENDPOINT_URL } from "../config";
import {
  AirtableRequestError,
  FetchError,
  JsonParseError,
  RequestError,
  WheeError
} from "../error";
import { PromiseResult } from "/lib/neverthrow-ext";

import { z, ZodType } from "zod";
import Airtable from "airtable";
import { AirtableBase } from "airtable/lib/airtable_base";

export type AirtableRecord = Record<string, object | string | number | boolean>;

type AirtableErrorMessage = {
  error: {
    type: string;
    message: string;
  };
};

// The store classes extend this class.
export default class AirtableApi {
  private apiKey: string = AIRTABLE_API_KEY;
  private baseId: string = AIRTABLE_BASE_ID;
  private endpointUrl: string = AIRTABLE_ENDPOINT_URL ?? "https://api.airtable.com";

  private jsClient!: AirtableBase;
  private static _instance: AirtableApi;

  // Singleton pattern constructor.
  constructor() {
    if (AirtableApi._instance) {
      return AirtableApi._instance;
    }

    this.jsClient = new Airtable({ apiKey: this.apiKey, endpointUrl: this.endpointUrl }).base(
      this.baseId
    );

    AirtableApi._instance = this;
  }

  private async getRecordCall(url: string) {
    const res = await fetch(url, {
      headers: {
        authorization: `Bearer ${AIRTABLE_API_KEY}`,
        "content-type": "application/json"
      },
      method: "GET"
    });

    if (!res.ok) {
      const { error }: AirtableErrorMessage = await res.json();
      const message = `${error.type}: ${error.message}`;
      console.error(`Request to airtable returned error: ${res.status}:\n${message}`);
      throw new RequestError(res.statusText, res.status);
    }

    return res;
  }

  // This version uses the Airtable client to communicate with Airtable.
  // IMPORTANT: This function can not be used in edge or middleware runtime. The node client uses dynamic code eval, which is not supported in those runtimes.
  //            Read more here: https://nextjs.org/docs/messages/edge-dynamic-code-evaluation
  //
  // todo(thusanarul): Change error type here?
  getTableRecords<V extends ZodType>(
    tableNameOrId: string,
    formula: string,
    schema: V,
    maxRecords: "all" | number = "all",
    sort: {
      field: string;
      direction: "asc" | "desc";
    } = { field: "createdAt", direction: "desc" }
  ): PromiseResult<z.infer<V>[], FetchError> {
    const call = async () => {
      let selection: {
        filterByFormula: string;
        sort: {
          field: string;
          direction: "asc" | "desc";
        }[];
        maxRecords?: number;
      } = {
        filterByFormula: formula.trim(),
        sort: [sort]
      };

      // maxRecords is either undefined, or a number: https://github.com/Airtable/airtable.js/blob/899adb414ebc789aa3b0dcfe6c19113377315564/src/query_params.ts#L73
      if (maxRecords !== "all") {
        selection = {
          ...selection,
          maxRecords: maxRecords
        };
      }

      const res = await this.jsClient
        .table(tableNameOrId)
        .select(selection)
        .all()
        .then((records) => {
          return records.map((val) => {
            const parsed = schema.safeParse(val);

            if (!parsed.success) {
              console.error(`Error when parsing data from Airtable: ${parsed.error.message}`);
              throw JsonParseError.generateFromUnknown(parsed.error, val);
            }
            return parsed.data;
          });
        });

      return res;
    };

    return PromiseResult.generate(() => call(), WheeError.generateFromUnknown);
  }

  // docs: https://airtable.com/developers/web/api/list-records
  getTableRecord<V extends ZodType>(
    {
      tableNameOrId,
      formula,
      view,
      sort = [{ field: "createdAt", direction: "desc" }]
    }: {
      tableNameOrId: string;
      formula?: string;
      view?: string;
      // These were the params that were sent in an old function that called on Airtable API.
      // Seems sensible, so let's keep it.
      sort?: { field: string; direction: string }[];
    },
    schema: V
  ): PromiseResult<z.infer<V> | null, FetchError> {
    const url = new URL(`${this.endpointUrl}/v0/${AIRTABLE_BASE_ID}/${tableNameOrId}`);
    url.searchParams.append("maxRecords", "1");

    if (formula) {
      url.searchParams.append("filterByFormula", formula.trim());
    }

    if (view) {
      url.searchParams.append("view", view);
    }

    if (sort) {
      sort.forEach((s, i) => {
        url.searchParams.append(`sort[${i}][field]`, s.field);
        url.searchParams.append(`sort[${i}][direction]`, s.direction);
      });
    }

    const call = async (url: string) => {
      return (await this.getRecordCall(url))
        .json()
        .then((val) => {
          const parsed = z.object({ records: z.array(schema) }).safeParse(val);

          if (!parsed.success) {
            console.error("Error when parsing data from Airtable: ", parsed.error.message);
            throw parsed.error;
          }

          if (parsed.data.records.length == 0) {
            return null;
          }

          return parsed.data.records[0];
        })
        .catch(JsonParseError.generateFromUnknown);
    };

    return PromiseResult.generate(() => call(url.toString()), WheeError.generateFromUnknown);
  }

  // docs: https://airtable.com/developers/web/api/get-record
  getTableRecordById<V extends ZodType>(
    tableNameOrId: string,
    recordId: string,
    schema: V
  ): PromiseResult<z.infer<V>, FetchError> {
    const call = async () => {
      const res = await this.jsClient
        .table(tableNameOrId)
        .find(recordId)
        .then((record) => {
          const parsed = schema.safeParse(record);

          if (!parsed.success) {
            console.error(`Error when parsing data from Airtable: ${parsed.error.message}`);
            throw JsonParseError.generateFromUnknown(parsed.error, record);
          }
          return parsed.data;
        });

      return res;
    };

    return new PromiseResult((resolve, reject) => {
      call().then(resolve).catch(reject);
    }, WheeError.generateFromUnknown);
  }

  // docs: https://airtable.com/developers/web/api/update-multiple-records
  upsertTableRecords = (
    tableNameOrId: string,
    records: AirtableRecord[],
    mergeFields: string[],
    typecast = false
  ): PromiseResult<Response, FetchError> => {
    const call = async () => {
      const url = `${this.endpointUrl}/v0/${this.baseId}/${tableNameOrId}`;

      const body = JSON.stringify({
        performUpsert: {
          fieldsToMergeOn: mergeFields
        },
        records: records,
        typecast: typecast
      });

      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${this.apiKey}`,
          "content-type": "application/json"
        },
        method: "PATCH", // A patch request will only update the fields included in the request
        body: body
      });

      if (!res.ok) {
        const { error }: AirtableErrorMessage = await res.json();
        const message = `${error.type}: ${error.message}`;
        console.error(`Request to airtable returned error: ${res.status}:\n${message}`);
        throw new RequestError(res.statusText, res.status, body);
      }

      return res;
    };

    return new PromiseResult((resolve, reject) => {
      call().then(resolve).catch(reject);
    }, WheeError.generateFromUnknown);
  };

  // docs: https://airtable.com/developers/web/api/update-record
  updateRecord<V extends ZodType>(
    tableNameOrId: string,
    recordId: string,
    fields: AirtableRecord,
    schema: V,
    typecast = false
  ): PromiseResult<z.infer<V>, FetchError> {
    const call = async () => {
      const url = `${this.endpointUrl}/v0/${this.baseId}/${tableNameOrId}/${recordId}`;

      const body = JSON.stringify({
        fields: fields,
        typecast: typecast
      });

      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${this.apiKey}`,
          "content-type": "application/json"
        },
        method: "PATCH", // A patch request will only update the fields included in the request
        body: body
      });

      if (!res.ok) {
        const { error }: AirtableErrorMessage = await res.json();
        const message = `${error.type}: ${error.message}`;
        console.error(`Request to airtable returned error: ${res.status}:\n${message}`);
        throw new RequestError(res.statusText, res.status, body);
      }

      return res
        .json()
        .then((val) => {
          const parsed = schema.safeParse(val);

          if (!parsed.success) {
            console.error("Error when parsing data from Airtable: ", parsed.error.message);
            throw parsed.error;
          }

          return parsed.data;
        })
        .catch(JsonParseError.generateFromUnknown);
    };

    return new PromiseResult((resolve, reject) => {
      call().then(resolve).catch(reject);
    }, WheeError.generateFromUnknown);
  }

  // docs: https://airtable.com/developers/web/api/create-records
  createRecord<V extends ZodType>(
    tableName: string,
    fields: AirtableRecord,
    schema: V,
    typecast = false
  ): PromiseResult<z.infer<V>, FetchError> {
    const call = async () => {
      const url = `${this.endpointUrl}/v0/${this.baseId}/${tableName}`;

      const body = JSON.stringify({
        fields: fields,
        typecast: typecast
      });

      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${this.apiKey}`,
          "content-type": "application/json"
        },
        method: "POST",
        body: body
      });

      if (!res.ok) {
        const { error }: AirtableErrorMessage = await res.json();
        const message = `${error.type}: ${error.message}`;
        console.error(`Request to airtable returned error: ${res.status}:\n${message}`);
        throw new AirtableRequestError(res.statusText, res.status, tableName, body);
      }

      return res
        .json()
        .then((val) => {
          const parsed = schema.safeParse(val);

          if (!parsed.success) {
            console.error("Error when parsing data from Airtable: ", parsed.error.message);
            throw parsed.error;
          }

          return parsed.data;
        })
        .catch(JsonParseError.generateFromUnknown);
    };

    return new PromiseResult((resolve, reject) => {
      call().then(resolve).catch(reject);
    }, WheeError.generateFromUnknown);
  }

  // docs: https://airtable.com/developers/web/api/create-records
  createRecords<V extends ZodType>(
    tableNameOrId: string,
    records: AirtableRecord[],
    schema: V,
    typecast = false
  ): PromiseResult<z.infer<V>, FetchError> {
    const call = async () => {
      const url = `${this.endpointUrl}/v0/${this.baseId}/${tableNameOrId}`;

      const body = JSON.stringify({
        records: records,
        typecast: typecast
      });

      const res = await fetch(url, {
        headers: {
          authorization: `Bearer ${this.apiKey}`,
          "content-type": "application/json"
        },
        method: "POST",
        body: body
      });

      if (!res.ok) {
        const { error }: AirtableErrorMessage = await res.json();
        const message = `${error.type}: ${error.message}`;
        console.error(
          `Request to airtable returned error: ${res.status}:\n${message}\nBody: ${body}`
        );

        throw new AirtableRequestError(message, res.status, tableNameOrId, body);
      }

      return res
        .json()
        .then((val) => {
          const parsed = schema.safeParse(val);

          if (!parsed.success) {
            console.error("Error when parsing data from Airtable: ", parsed.error.message);
            throw parsed.error;
          }

          return parsed.data;
        })
        .catch(JsonParseError.generateFromUnknown);
    };

    return new PromiseResult((resolve, reject) => {
      call().then(resolve).catch(reject);
    }, WheeError.generateFromUnknown);
  }
}
